// ─────────────────────────────────────────────────────────────────────────────
// src/hooks/repos/sedationDetailRepo.ts
// ─────────────────────────────────────────────────────────────────────────────
import { createRepo } from "./createRepo";
import { BaseEntity, MedicalHistoryFieldEntity, Patient, RepositoryOptions, PaginatedResponse } from "../../types/index";
import { FieldOfSurgery } from "../../api/generated/models/FieldOfSurgery";

// Supporting interfaces (moved above SedationDetail so they are defined before use)
export interface BloodPressure {
  Systolic: number | null;
  Diastolic: number | null;
}

export interface Cannulation {
  Size: string | null;
  Type: string | null;
  Site: string | null;
  TimeHrs: number | null;
  TimeMins: number | null;
}

// Extended SedationDetail interface with all specific fields
export interface SedationDetail extends BaseEntity {
  PatientId: string;
  Patient?: Patient; // optional embedded ref, not stored server-side typically

  // Basic sedation info
  Reason: string | null;
  Procedure: string | null;
  Surgeon: string | null;
  ConsentSigned: boolean;
  Sedationist: string | null;
  Nurse: string | null;

  // Surgery field information
  FieldOfSurgery: FieldOfSurgery;
  FieldOfSurgeryNote: string | null;

  // Status and notes
  Status: "Pending" | "InProgress" | "Completed";
  Comments: string | null;
  Considerations: string | null;

  // Vital signs
  BloodPressure: BloodPressure;
  PulseRate: number | null;
  CO2EndTidal: number | null;
  ECG: boolean;
  SAO2: number | null;
  RespiratoryRate: number | null;
  ReplayCount: number;
  BIS: number | null;
  
  // Equipment/Setup checkboxes
  CapnographyAttached: boolean;
  BISAttached: boolean;
  BlanketOverPatient: boolean;

  // Cannulation details
  Cannulation: Cannulation;

  // File management
  PdfUrl: string | null;
  PatientPackUrl: string | null;
  FileType: "None" | "URL" | "Invalid" | null;

  // Staff information
  SedationistId: string | null;
  SedationistEmail: string | null;

  // Appointment
  AppointmentId: string | null;
  AppointmentStartDateTime: string | null; // ISO string
  AppointmentEndDateTime: string | null;

  // Clinic information
  ClinicName: string;
  ClinicPhoneNumber: string;

  // New optional key points review (stored as JSON/BLOB)
  KeyPoints?: KeyPointsReview | null;
  
  // Suitability Review Information (IOSN)
  SuitabilityReviewInformation?: SuitabilityReviewInformation | null;

  // Health History Snapshot (Medical Legal Lock)
  HealthHistorySnapshot?: HealthHistorySnapshot | null;
}

export interface HealthHistorySnapshot {
  MedicalHistory: string | null;
  Medications: string | null;
  Allergies: string | null;
  AnaestheticHistory: string | null;
  ASAClassification: string | null;
  Height: string | null;
  Weight: string | null;
  BMI: string | null;
  Smoking: string | null;
  Alcohol: string | null;
  /**
   * The structured history as at capture time. Optional: snapshots stored
   * before this existed — and any that come back from the server, whose DTO has
   * no structured field — simply fall back to the legacy strings above.
   */
  MedicalHistoryFields?: MedicalHistoryFieldEntity[] | null;
  SnapshotDate: string; // ISO string
}

/**
 * The one place a snapshot is built. Five screens capture the medical-legal
 * lock and each used to carry its own copy of this literal, so a field added to
 * the snapshot could be forgotten at four of them and still ship green.
 */
export function buildHealthHistorySnapshot(patient: Patient): HealthHistorySnapshot {
  return {
    MedicalHistory: patient.MedicalHistory || null,
    Medications: patient.Medications || null,
    Allergies: patient.Allergies || null,
    AnaestheticHistory: patient.AnestheticHistory || null,
    ASAClassification: patient.ASAClassification?.toString() || null,
    Height: patient.Height?.toString() || null,
    Weight: patient.Weight?.toString() || null,
    BMI: patient.BMI?.toString() || null,
    Smoking: patient.SmokingStatus || null,
    Alcohol: patient.AlcoholStatus || null,
    // Freeze the structured history too, or a later edit to it would change
    // what a regenerated PDF prints.
    MedicalHistoryFields: patient.MedicalHistoryFields || null,
    SnapshotDate: new Date().toISOString(),
  };
}

// Key Points related interfaces
export interface KeyPointsReview {
  PreSedation: PreSedationChecks;
  Technique: SedationTechniqueExplained;
  Consent: ConsentConfirmation;
  Notes?: string | null;
  RecordedAt?: string | null; // ISO
  RecordedBy?: string | null;
}

export type SedationLevel = 'Minimal' | 'Moderate' | 'Deep' | 'General';

export interface PreSedationChecks {
  InformationPackReceived: boolean;
  PreOpExplanationGiven: boolean;
  PatientUnderstandsProcedure: boolean;
}

export interface SedationTechniqueExplained {
  IntendedLevel: SedationLevel; // default Moderate
  NotAsleepOrUnconscious: boolean;
  AimAnxiolysisWithResponsiveness: boolean;
  PossibleAmnesiaExplained: boolean;
  LocalAnaestheticBySurgeon: boolean;
  RisksExplainedPONVAndComplications: boolean;
  EscortArrangementsConfirmed: boolean;
  PostSedationInstructionsGiven: boolean;
}

export interface ConsentConfirmation {
  WrittenConsentReconfirmedVerbally: boolean;
  PatientHappyToProceed: boolean;
}

// Suitability Review Information (IOSN - Index of Sedation Needs)
export interface SuitabilityReviewInformation {
  IOSNScore: number | null; // Sum of all 5 items, range 5-25
  DiscussInConfidenceWithSedationist: boolean | null;
  FeelFearAnxietyDistressRequestedSedation: boolean | null;
  DoctorRecommendedSedation: boolean | null;
}

// Create the repository instance
export const sedationDetailRepo = createRepo<SedationDetail>({
  storeName: "sedationDetails",
  entityName: "SedationDetail",
  skipOutbox: true, // Skip automatic outbox - only SedationCompletion should trigger sync
});

// Factory function to build a SedationDetail with defaults
export function buildSedationDetail(
  partial: Partial<SedationDetail> = {}
): Partial<SedationDetail> {
  return {
    Reason: null,
    Procedure: null,
    Surgeon: null,
    ConsentSigned: false,
    Sedationist: null,
    Nurse: null,
    FieldOfSurgery: FieldOfSurgery.GENERAL_DENTISTRY,
    FieldOfSurgeryNote: null,
    Status: "Pending", // 'Pending' | 'InProgress' | 'Completed'
    Comments: null,
    Considerations: null,
    PatientId: "", // REQUIRED - will be overridden
    Patient: undefined, // optional embedded ref, not stored server-side typically
    BloodPressure: { Systolic: null, Diastolic: null },
    PulseRate: null,
    CO2EndTidal: null,
    ECG: false,
    SAO2: null,
    RespiratoryRate: null,
    ReplayCount: 0,
    BIS: null,
    CapnographyAttached: false,
    BISAttached: false,
    BlanketOverPatient: false,
    Cannulation: {
      Size: null,
      Type: null,
      Site: null,
      TimeHrs: null,
      TimeMins: null,
    },
    PdfUrl: null,
    PatientPackUrl: null,
    FileType: null, // 'None' | 'URL' | 'Invalid'
    SedationistId: null,
    SedationistEmail: null,
    AppointmentId: null,
    AppointmentStartDateTime: null, // ISO
    AppointmentEndDateTime: null,
    ClinicName: "",
    ClinicPhoneNumber: "",
    KeyPoints: {
      PreSedation: {
        InformationPackReceived: true,
        PreOpExplanationGiven: true,
        PatientUnderstandsProcedure: true,
      },
      Technique: {
        IntendedLevel: 'Moderate',
        NotAsleepOrUnconscious: true,
        AimAnxiolysisWithResponsiveness: true,
        PossibleAmnesiaExplained: true,
        LocalAnaestheticBySurgeon: true,
        RisksExplainedPONVAndComplications: true,
        EscortArrangementsConfirmed: true,
        PostSedationInstructionsGiven: true,
      },
      Consent: {
        WrittenConsentReconfirmedVerbally: true,
        PatientHappyToProceed: true,
      },
      Notes: null,
      RecordedAt: null,
      RecordedBy: null,
    },
    SuitabilityReviewInformation: {
      IOSNScore: null,
      DiscussInConfidenceWithSedationist: null,
      FeelFearAnxietyDistressRequestedSedation: null,
      DoctorRecommendedSedation: null,
    },
    HealthHistorySnapshot: null,
    ...partial,
  };
}

// Convenience methods for sedation-specific operations with full type safety
export const getSedations = async (
  options: RepositoryOptions & { patientId?: string } = {}
): Promise<PaginatedResponse<SedationDetail>> => {
  if (options.patientId) {
    // For patient-specific queries, get all and filter manually
    const allSedations = await getSedationsByPatientId(options.patientId);
    // Convert to paginated response format
    const page = options.page || 1;
    const pageSize = options.pageSize || 10;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = allSedations.slice(startIndex, endIndex);
    const totalPages = Math.ceil(allSedations.length / pageSize);
    
    return {
      data: paginatedItems,
      total: allSedations.length,
      page: page,
      pageSize: pageSize,
      totalPages: totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1
    };
  }
  return sedationDetailRepo.listPaged(options);
};

export const getSedationById = async (id: string): Promise<SedationDetail | null> => {
  try {
    const result = await sedationDetailRepo.getById(id);
    return result;
  } catch (error) {
    console.error("❌ sedationDetailRepo.getSedationById: Error:", error);
    throw error;
  }
};

export const createSedation = (
  data: Omit<SedationDetail, "Id" | "CreatedDateTime">
): Promise<SedationDetail> => sedationDetailRepo.create(data);

export const updateSedation = async (
  id: string,
  data: Partial<SedationDetail>
): Promise<SedationDetail> => {
  try {
    const result = await sedationDetailRepo.update(id, data);
    return result;
  } catch (error) {
    console.error("❌ sedationDetailRepo.updateSedation: Update failed:", error);
    throw error;
  }
};

export const deleteSedation = (id: string): Promise<boolean> =>
  sedationDetailRepo.softDelete(id);

// Never let a re-import/sync payload erase KeyPoints/MDAS/health-history data
// that's already saved locally just because the incoming record omits it.
function preserveReviewFields(
  incoming: Partial<SedationDetail>,
  existing: SedationDetail
): Partial<SedationDetail> {
  return {
    ...incoming,
    KeyPoints: incoming.KeyPoints ?? existing.KeyPoints,
    SuitabilityReviewInformation: incoming.SuitabilityReviewInformation ?? existing.SuitabilityReviewInformation,
    HealthHistorySnapshot: mergeHealthHistorySnapshot(
      incoming.HealthHistorySnapshot,
      existing.HealthHistorySnapshot
    ),
  };
}

/**
 * The server's snapshot DTO has no structured field, so a refreshed record
 * comes back carrying only the legacy strings. Taking it whole would silently
 * unfreeze the structured history the device locked — keep what we already hold.
 */
function mergeHealthHistorySnapshot(
  incoming: HealthHistorySnapshot | null | undefined,
  existing: HealthHistorySnapshot | null | undefined
): HealthHistorySnapshot | null | undefined {
  if (!incoming) return existing;
  if (incoming.MedicalHistoryFields) return incoming;
  if (!existing?.MedicalHistoryFields) return incoming;
  return { ...incoming, MedicalHistoryFields: existing.MedicalHistoryFields };
}

// Enhanced upsert function that prevents duplicates (Requirement 2)
export const upsertSedationWithDuplicateCheck = async (
  sedationData: Partial<SedationDetail>,
  forceNewCreation: boolean = false
): Promise<{ sedation: SedationDetail; isNewCreation: boolean }> => {
  // If we have an ID, try to update existing
  if (sedationData.Id) {
    const existingSedation = await getSedationById(sedationData.Id);
    if (existingSedation) {
      // ModifiedDateTime is automatically set by the repository layer
      const updatedSedation = await updateSedation(sedationData.Id, preserveReviewFields(sedationData, existingSedation));
      return { sedation: updatedSedation, isNewCreation: false };
    }
  }

  // CRITICAL: If forceNewCreation is true, skip duplicate check and create new sedation
  if (forceNewCreation) {
    const newSedation = await createSedation(sedationData as Omit<SedationDetail, "Id" | "CreatedDateTime">);
    return { sedation: newSedation, isNewCreation: true };
  }

  // Check for existing sedation by PatientId and Status to prevent duplicates
  // ONLY when not forcing new creation (this is for sync/import scenarios)
  if (sedationData.PatientId) {
    const existingSedations = await getSedationsByPatientId(sedationData.PatientId);
    
    // Check for existing non-completed sedations for this patient
    const activeSedations = existingSedations.filter(s => 
      s.Status !== 'Completed' && !s.Deactivated
    );

    if (activeSedations.length > 0) {
      // Update the most recent active sedation instead of creating a new one
      const mostRecentSedation = activeSedations.sort((a, b) => 
        new Date(b.CreatedDateTime || '').getTime() - new Date(a.CreatedDateTime || '').getTime()
      )[0];

      const updatedSedation = await updateSedation(mostRecentSedation.Id, {
        ...preserveReviewFields(sedationData, mostRecentSedation),
        Id: mostRecentSedation.Id, // Ensure we keep the existing ID
        ModifiedDateTime: new Date().toISOString()
      });
      return { sedation: updatedSedation, isNewCreation: false };
    }
  }

  // No existing sedation found, create new one
  const newSedation = await createSedation(sedationData as Omit<SedationDetail, "Id" | "CreatedDateTime">);
  return { sedation: newSedation, isNewCreation: true };
};

// Sync-specific sedation operations
export const upsertSedation = async (sedationData: Partial<SedationDetail>): Promise<SedationDetail> => {
  if (!sedationData.Id) {
    // Create new sedation
    return await sedationDetailRepo.create(sedationData as Omit<SedationDetail, "Id" | "CreatedDateTime">);
  }

  // Check if sedation exists locally
  const existingSedation = await sedationDetailRepo.getById(sedationData.Id);
  
  if (existingSedation) {
    // CRITICAL PROTECTION: If local sedation exists, preserve it and skip API overwrite
    // This prevents loss of local edits and user data
   
    // Only update sync status markers without touching actual data
    const preservedData = {
      ...existingSedation, // Keep ALL existing local data
      Synced: true, // Mark as synced
      LastSyncedAt: new Date().toISOString(), // Update sync timestamp
      // DO NOT update ModifiedDateTime - preserve local modification time
      // DO NOT update PendingSync - let existing value remain
    };
    
    return await sedationDetailRepo.update(sedationData.Id, preservedData);
  } else {
    // Create new sedation with API data
    return await sedationDetailRepo.create({
      ...sedationData,
      Synced: true,
      LastSyncedAt: new Date().toISOString(),
      PendingSync: null,
    } as Omit<SedationDetail, "Id" | "CreatedDateTime">);
  }
};

export const markSedationForSync = async (
  id: string, 
  operation: 'create' | 'update' | 'delete'
): Promise<void> => {
  await sedationDetailRepo.update(id, {
    Synced: false,
    PendingSync: operation,
    ModifiedDateTime: new Date().toISOString(),
  });
};

export const getPendingSyncSedations = async (): Promise<SedationDetail[]> => {
  const allSedations = await sedationDetailRepo.listAll();
  return allSedations.filter(sedation => 
    sedation.PendingSync && !sedation.Synced
  );
};

// Additional sedation-specific query methods with type safety
export const getSedationsByPatientId = async (
  patientId: string
): Promise<SedationDetail[]> => {
  const allSedations = await sedationDetailRepo.listAll();
  return allSedations.filter(
    (sedation) => sedation.PatientId === patientId
  ).sort((a,b) =>new Date(a.CreatedDateTime).getTime() - new Date(b.CreatedDateTime).getTime());
};

export const getSedationsByStatus = async (
  status: "Pending" | "InProgress" | "Completed"
): Promise<SedationDetail[]> => {
  const allSedations = await sedationDetailRepo.listAll();
  return allSedations.filter(
    (sedation) => sedation.Status === status
  );
};

export const getSedationsBySedationist = async (
  sedationistId: string
): Promise<SedationDetail[]> => {
  const allSedations = await sedationDetailRepo.listAll();
  return allSedations.filter(
    (sedation) => sedation.SedationistId === sedationistId
  );
};

export const getActiveSedations = async (): Promise<SedationDetail[]> => {
  const allSedations = await sedationDetailRepo.listAll();
  return allSedations.filter(
    (sedation) => !sedation.Deactivated && sedation.Status !== "Completed"
  );
};

export const getCompletedSedations = async (
  patientId?: string
): Promise<SedationDetail[]> => {
  const allSedations = await sedationDetailRepo.listAll();
  return allSedations.filter(
    (sedation) => 
      sedation.Status === "Completed" && 
      (!patientId || sedation.PatientId === patientId)
  );
};

export const searchSedationsByProcedure = async (
  searchTerm: string
): Promise<SedationDetail[]> => {
  const allSedations = await sedationDetailRepo.listAll();
  return allSedations.filter(
    (sedation) =>
      (sedation.Procedure &&
        sedation.Procedure.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (sedation.Reason &&
        sedation.Reason.toLowerCase().includes(searchTerm.toLowerCase()))
  );
};

// Helper function to get the most recent sedation for a patient
export const getLatestSedationForPatient = async (
  patientId: string
): Promise<SedationDetail | null> => {
  const patientSedations = await getSedationsByPatientId(patientId);
  if (patientSedations.length === 0) return null;
  
  // Sort by CreatedDateTime descending to get the most recent
  patientSedations.sort((a, b) => 
    new Date(b.CreatedDateTime || '').getTime() - 
    new Date(a.CreatedDateTime || '').getTime()
  );
  
  return patientSedations[0];
};

// Helper function to check if a patient has any active sedations
export const hasActiveSedations = async (patientId: string): Promise<boolean> => {
  const patientSedations = await getSedationsByPatientId(patientId);
  return patientSedations.some(sedation => 
    sedation.Status === "Pending" || sedation.Status === "InProgress"
  );
};

// Export the repository instance for direct access if needed
export { sedationDetailRepo as default };
